import * as React from 'react';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@components/atoms';
import { cn } from '@utils';

/**
 * Configuration options for the icon display mode
 * @interface IconOptions
 * @property {('none'|'avatar')} [mode='none'] - Display mode for the icon
 *   - 'none': Default display without special styling
 *   - 'avatar': Applies rounded-sm corners and overflow handling for avatar-style display
 */
interface IconOptions {
	mode?: 'none' | 'avatar';
	align?: 'start' | 'end' | 'baseline' | 'center';
}

/**
 * Props for the ContentCell component
 * @interface ContentCellProps
 *
 * @property {React.ReactNode} [icon] - Optional icon element to display at the start of the cell
 *   Can be any valid React node (SVG, image, component, etc.)
 *
 * @property {IconOptions} [iconOptions={ mode: 'none' }] - Configuration for icon display
 *   Controls how the icon is styled and presented
 *
 * @property {string} [moreContent] - Additional content displayed below primary/secondary content
 *   Rendered as underlined text with truncation if too long
 *
 * @property {React.ReactNode} [endContent] - Optional content displayed after the text column
 *   Rendered as a shrink-wrapped sibling of the text stack (mirrors `icon`)
 *
 * @property {React.ReactNode} [moreContentHover] - Content shown in tooltip when hovering over moreContent
 *   Only displayed if moreContent is also provided
 *
 * @property {string} [moreContentLink] - URL that moreContent links to when clicked
 *   When provided:
 *   - Makes moreContent clickable
 *   - Adds hover state color change
 *   - Wraps content in an anchor tag
 *
 * @property {string} [primaryContent] - Main content displayed at the top of the cell
 *   Rendered in Bond blue with slightly larger text
 *
 * @property {React.ReactNode} [primaryContentHover] - Content shown in tooltip when hovering over primaryContent
 *   Only displayed if primaryContent is also provided
 *
 * @property {string|string[]} [secondaryContent] - Supporting content displayed below primaryContent
 *   Can be either:
 *   - Single string: Rendered as one line
 *   - Array of strings: Each string rendered as separate line
 *   Both forms rendered in gray with smaller text
 */
export interface ContentCellProps {
	icon?: React.ReactNode;
	iconOptions?: IconOptions;
	endContent?: React.ReactNode;
	moreContent?: string;
	moreContentHover?: React.ReactNode;
	moreContentLink?: string;
	primaryContent?: string;
	primaryContentHover?: React.ReactNode;
	secondaryContent?: string | string[];
	dataTestId?: string;
	dataReservationId?: string | number;
}

/**
 * A flexible content cell component for displaying hierarchical information with optional icon and hover states.
 * Enhanced with tooltip support for truncated content.
 */
export const ContentCell: React.FC<ContentCellProps> = ({
	icon,
	iconOptions = { mode: 'none' },
	endContent,
	moreContent,
	moreContentHover,
	moreContentLink,
	primaryContent,
	primaryContentHover,
	secondaryContent,
	dataTestId,
	dataReservationId,
}) => {
	// Refs to check for text overflow
	const primaryRef = React.useRef<HTMLDivElement>(null);
	const secondaryRefs = React.useRef<(HTMLDivElement | null)[]>([]);
	const moreContentRef = React.useRef<HTMLDivElement>(null);

	// Track overflow state for each content type
	const [isPrimaryOverflowing, setIsPrimaryOverflowing] = React.useState(false);
	const [secondaryOverflowing, setSecondaryOverflowing] = React.useState<boolean[]>([]);
	const [isMoreContentOverflowing, setIsMoreContentOverflowing] = React.useState(false);

	// Check for overflow in the primary content
	React.useEffect(() => {
		if (primaryRef.current) {
			setIsPrimaryOverflowing(primaryRef.current.scrollWidth > primaryRef.current.clientWidth);
		}
	}, [primaryContent]);

	// Check for overflow in the secondary content
	React.useEffect(() => {
		if (!secondaryContent) return;

		const newOverflowStates: boolean[] = [];

		if (Array.isArray(secondaryContent)) {
			secondaryContent.forEach((_, index) => {
				const ref = secondaryRefs.current[index];
				if (ref) {
					newOverflowStates[index] = ref.scrollWidth > ref.clientWidth;
				} else {
					newOverflowStates[index] = false;
				}
			});
		} else {
			const ref = secondaryRefs.current[0];
			if (ref) {
				newOverflowStates[0] = ref.scrollWidth > ref.clientWidth;
			}
		}

		setSecondaryOverflowing(newOverflowStates);
	}, [secondaryContent]);

	// Check for overflow in the more content
	React.useEffect(() => {
		if (moreContentRef.current) {
			setIsMoreContentOverflowing(moreContentRef.current.scrollWidth > moreContentRef.current.clientWidth);
		}
	}, [moreContent]);

	/**
	 * Wraps content in a tooltip if it's overflowing
	 */
	const wrapWithTooltipIfNeeded = (
		content: React.ReactNode,
		isOverflowing: boolean,
		tooltipContent?: React.ReactNode
	) => {
		if (!isOverflowing) return content;

		return (
			<Tooltip>
				<TooltipTrigger asChild>{content}</TooltipTrigger>
				<TooltipContent>{tooltipContent || content}</TooltipContent>
			</Tooltip>
		);
	};

	/**
	 * Renders the primary content with tooltip if needed
	 */
	const renderPrimaryContent = () => {
		if (!primaryContent) return null;

		const content = (
			<div ref={primaryRef} className="truncate text-sm text-primary">
				{primaryContent}
			</div>
		);

		const tooltipContent = primaryContentHover || primaryContent;

		const wrappedContent = wrapWithTooltipIfNeeded(
			content,
			isPrimaryOverflowing || !!primaryContentHover,
			tooltipContent
		);

		return wrappedContent;
	};

	/**
	 * Renders the secondary content.
	 */
	const renderSecondaryContent = () => {
		if (!secondaryContent) return null;

		if (Array.isArray(secondaryContent)) {
			return secondaryContent.map((content, index) => {
				const secondaryContent = (
					<div key={index} ref={el => (secondaryRefs.current[index] = el)} className="truncate text-xs text-secondary">
						{content}
					</div>
				);

				return wrapWithTooltipIfNeeded(secondaryContent, secondaryOverflowing[index] || false, content);
			});
		}

		const singleSecondaryContent = (
			<div ref={el => (secondaryRefs.current[0] = el)} className="truncate text-xs text-secondary">
				{secondaryContent}
			</div>
		);

		return wrapWithTooltipIfNeeded(singleSecondaryContent, secondaryOverflowing[0] || false, secondaryContent);
	};

	/**
	 * Renders the more content with tooltip.
	 */
	const renderMoreContent = () => {
		if (!moreContent) return null;

		const content = (
			<div
				ref={moreContentRef}
				className={cn(
					'truncate text-xs text-secondary underline',
					moreContentLink && 'cursor-pointer hover:text-primary'
				)}
			>
				{moreContent}
			</div>
		);

		// If there's already a specific hover content, use that
		// Otherwise, show the truncated text in the tooltip
		const tooltipContent = moreContentHover || moreContent;

		const wrappedContent = wrapWithTooltipIfNeeded(
			content,
			isMoreContentOverflowing || !!moreContentHover,
			tooltipContent
		);

		// If there's a link, wrap the content in an anchor
		return moreContentLink ? (
			<a href={moreContentLink} className="block">
				{wrappedContent}
			</a>
		) : (
			wrappedContent
		);
	};

	const hasAvaterIcon: boolean = iconOptions?.mode === 'avatar';
	const itemsAlign = `items-${iconOptions?.align || 'start'}`; 

	return (
		<TooltipProvider delayDuration={0} skipDelayDuration={0}>
			<div
				data-aid="data-grid-content-cell"
				data-testid={dataTestId}
				data-reservation-id={dataReservationId}
				className={cn('flex items-start gap-2', itemsAlign)}
			>
				{icon && <div className={cn('shrink-0', hasAvaterIcon && 'overflow-hidden rounded-full')}>{icon}</div>}
				<div className="flex min-w-0 flex-col">
					{renderPrimaryContent()}
					{renderSecondaryContent()}
					{renderMoreContent()}
				</div>
				{endContent && <div className="shrink-0">{endContent}</div>}
			</div>
		</TooltipProvider>
	);
};
